import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn, type MenuPage } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuOrientation, MenuSection } from '../types.js';
import {
  badge,
  bilingual,
  description,
  fitScale,
  image,
  listCss,
  listItem,
  listItemHeight,
  price,
  sectionTitle,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Pizza Showcase, "Grid & sides": a pizzeria board on deep black. Round pizzas seen from above in
 * a grid, each with a yellow name and a little Medium / Large price table, and a red panel with a
 * slanted edge down the side listing the sides, with a photo at its foot. The board's last section
 * goes in the panel; everything before it fills the grid.
 */

const S = '.mb-t-pizza-show-grid';

/** Green, white and red bars: the Italian stripe under the brand. */
const STRIPE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 1' preserveAspectRatio='none'%3E%3Crect width='1' height='1' fill='%23169b4c'/%3E%3Crect x='1' width='1' height='1' fill='%23f4f4f4'/%3E%3Crect x='2' width='1' height='1' fill='%23d52b1e'/%3E%3C/svg%3E\")";
/** A sprig of basil beside the brand. */
const BASIL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cpath d='M30 62C29 48 30 34 36 20' stroke='%232f6f2a' stroke-width='2.4' fill='none' stroke-linecap='round'/%3E%3Cpath d='M35 22C22 22 11 13 10 3C24 2 35 10 35 22Z' fill='%233f9b35'/%3E%3Cpath d='M35 22C26 15 19 9 10 3' stroke='%23296324' stroke-width='1.2' fill='none'/%3E%3Cpath d='M36 24C46 15 58 15 63 22C55 33 43 33 36 24Z' fill='%234fb043'/%3E%3Cpath d='M36 24C45 22 54 21 63 22' stroke='%232f7a2a' stroke-width='1.2' fill='none'/%3E%3Cpath d='M31 40C20 44 9 39 5 29C17 25 27 30 31 40Z' fill='%23388d30'/%3E%3Cpath d='M31 40C22 36 13 32 5 29' stroke='%23245c20' stroke-width='1.2' fill='none'/%3E%3Cpath d='M32 44C40 37 51 38 56 45C49 53 38 52 32 44Z' fill='%2345a33b'/%3E%3C/svg%3E\")";

/** The panel's section and the grid's sections: the last section with items goes in the panel. */
function split(board: MenuBoardContent): {
  grid: { section: MenuSection; items: MenuItemContent[] }[];
  panel: { section: MenuSection; items: MenuItemContent[] } | null;
} {
  const groups = groupBySection(board).filter((g) => g.items.length > 0);
  if (groups.length < 2) return { grid: groups, panel: null };
  return { grid: groups.slice(0, -1), panel: groups[groups.length - 1]! };
}

/** Cards per page: four by two beside the panel, five by two without; three by three portrait. */
function gridCapacity(orientation: MenuOrientation, panel: boolean): number {
  if (orientation === 'portrait') return panel ? 9 : 12;
  return panel ? 8 : 10;
}

function panelColumn(board: MenuBoardContent, orientation: MenuOrientation): MenuColumn {
  const item = { nameEm: 1.55, descEm: 0.8, perLine: 1000, gap: 1.05 };
  return {
    capacity: orientation === 'portrait' ? 25 : 43,
    blockCost: (_section, items) => items.reduce((sum, i) => sum + listItemHeight(board, i, item), 0),
  };
}

function priceTable(ctx: RenderContext, item: MenuItemContent): string {
  if (item.variants.length) {
    return item.variants
      .slice(0, 3)
      .map(
        (v) =>
          `<div class="psg-row"><span class="psg-lbl">${variantLabel(ctx, v.label, v.labelAr)}</span><span class="psg-amt" dir="ltr">${price(ctx, v.price)}</span></div>`,
      )
      .join('');
  }
  const old =
    item.discountPrice !== null && item.discountPrice < item.price
      ? `<s class="psg-old" dir="ltr">${price(ctx, item.price)}</s>`
      : '';
  const now = old ? item.discountPrice! : item.price;
  return `<div class="psg-row psg-one">${old}<span class="psg-amt" dir="ltr">${price(ctx, now)}</span></div>`;
}

function card(ctx: RenderContext, item: MenuItemContent, tag: string): string {
  const photo = image(ctx, item.imageMediaId, 'psg-img');
  const initial = escapeHtml((item.title.trim()[0] ?? '?').toUpperCase());
  const badges = badge(ctx, item, 'psg-badge');
  const scale = fitScale(ctx.showEn ? item.title : item.titleAr || item.title, 18);
  return [
    '<div class="psg-card">',
    `<div class="psg-phw"><div class="psg-ph">${photo || `<span class="psg-initial">${initial}</span>`}`,
    badges ? `<span class="psg-badges">${badges}</span>` : '',
    tag,
    '</div></div>',
    `<div class="psg-names" style="font-size:${scale.toFixed(2)}em">${bilingual(ctx, item.title, item.titleAr, 'psg-name')}</div>`,
    description(ctx, item, 'psg-desc'),
    `<div class="psg-prices">${priceTable(ctx, item)}</div>`,
    '</div>',
  ].join('');
}

/** The brand with the Italian stripe beneath and basil beside it; shared look of the set. */
function brand(ctx: RenderContext): string {
  const { board } = ctx;
  const name = board.brand.name?.trim() || board.brand.nameAr?.trim() ? board.brand : null;
  const text = name
    ? bilingual(ctx, board.brand.name, board.brand.nameAr, 'psg-bname')
    : bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'psg-bname');
  return `<div class="psg-brand"><div class="psg-bwords">${text}<span class="psg-stripe"></span></div><span class="psg-basil"></span></div>`;
}

export const pizzaShowGrid: MenuTemplate = {
  id: 'pizza-show-grid',
  name: 'Pizza showcase: grid & sides',
  description:
    'Round pizzas from above in a grid on black, each with Medium and Large prices, and a slanted red panel listing the sides with a photo. The last section goes in the panel.',
  defaultTheme: {
    backgroundColor: '#0b0b0b',
    surfaceColor: '#1b1b1b',
    textColor: '#ffffff',
    primaryColor: '#ffc72c',
    accentColor: '#d62828',
    fontId: 'bold',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'black-red', name: 'Black & red', theme: {} },
    { id: 'charcoal-green', name: 'Charcoal & green', theme: { backgroundColor: '#1d2023', surfaceColor: '#292d31', textColor: '#f3f3f1', primaryColor: '#ffd166', accentColor: '#1f7a4d' } },
    { id: 'navy-orange', name: 'Navy & orange', theme: { backgroundColor: '#0d1828', surfaceColor: '#16253b', textColor: '#f4f6fa', primaryColor: '#ffd23f', accentColor: '#e8590c' } },
    { id: 'cream-red', name: 'Cream paper & red', theme: { backgroundColor: '#f5ecdc', surfaceColor: '#fffaf0', textColor: '#2a211b', primaryColor: '#9e1b14', accentColor: '#c8102e' } },
  ],
  cuisines: ['pizza'],
  family: { id: 'pizza-show', name: 'Pizza Showcase', layout: 'Grid & sides' },
  uses: { featured: false, sectionImages: true, brand: true, decorations: [] },

  paginate(board, orientation) {
    const { grid, panel } = split(board);
    const capacity = gridCapacity(orientation, panel !== null);
    const gridPages = packColumns(grid, [{ capacity, blockCost: (_s, items) => items.length }]);
    const panelPages: MenuPage[] = panel ? packColumns([panel], [panelColumn(board, orientation)]) : [{ columns: [[]] }];
    const count = Math.max(gridPages.length, panelPages.length, 1);
    return Array.from({ length: count }, (_, i) => ({
      columns: [gridPages[i]?.columns[0] ?? [], panelPages[i]?.columns[0] ?? []],
    }));
  },

  render(ctx) {
    const { board } = ctx;
    const gridBlocks: MenuBlock[] = ctx.page.columns[0] ?? [];
    const panelBlocks: MenuBlock[] = ctx.page.columns[1] ?? [];
    const portrait = ctx.orientation === 'portrait';
    const { panel: panelGroup } = split(board);

    const showTags = gridBlocks.length > 1;
    const cards = gridBlocks.flatMap((b) =>
      b.items.map((item, i) => {
        const tag =
          showTags && i === 0 && !b.continued && (b.section.title?.trim() || b.section.titleAr?.trim())
            ? `<span class="psg-tag">${escapeHtml((ctx.showEn ? b.section.title : b.section.titleAr || b.section.title) ?? '')}</span>`
            : '';
        return card(ctx, item, tag);
      }),
    );
    const n = cards.length;
    const maxCols = portrait ? 3 : panelGroup ? 4 : 5;
    const cols = Math.max(1, Math.min(maxCols, n));
    const rows = Math.max(1, Math.ceil(n / cols));

    const panelItems = panelBlocks.flatMap((b) => b.items);
    let panel = '';
    if (panelItems.length && panelGroup) {
      const section = panelBlocks[0]!.section;
      const pic =
        section.imageMediaId ?? panelGroup.items.find((i) => i.imageMediaId)?.imageMediaId ?? null;
      const photo = image(ctx, pic, 'psg-pimg');
      panel = [
        '<aside class="psg-panel"><div class="psg-pin">',
        `<div class="psg-phead">${sectionTitle(ctx, section, 'psg-ptitle')}</div>`,
        `<div class="psg-list">${panelItems.map((i) => listItem(ctx, i, 'psgl')).join('')}</div>`,
        photo ? `<div class="psg-pphoto">${photo}</div>` : '',
        '</div></aside>',
      ].join('');
    }

    const title = bilingual(ctx, board.settings.title, board.settings.titleAr, 'psg-title');
    const header = `<div class="psg-head">${brand(ctx)}${title ? `<div class="psg-titles">${title}</div>` : ''}</div>`;
    const grid = n
      ? `<div class="psg-grid" style="--cols:${cols};--rows:${rows}">${cards.join('')}</div>`
      : panel
        ? '<div class="psg-grid"></div>'
        : '<div class="mb-empty">No pizzas on this board yet</div>';
    return `<div class="psg-wrap${panel ? ' psg-has-panel' : ''}"><div class="psg-main">${header}${grid}</div>${panel}</div>`;
  },

  css: `
${S}{padding:0;background-image:radial-gradient(ellipse at 35% 45%,rgba(255,255,255,.05),transparent 65%)}
${S} .psg-wrap{flex:1;min-height:0;display:flex}
${S}.mb-portrait .psg-wrap{flex-direction:column}
${S} .psg-main{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;padding:2.4em 2.6em 2.2em 3.2em}
${S}.mb-portrait .psg-main{padding:2.6em 2.6em 1.6em}

${S} .psg-head{display:flex;align-items:flex-start;justify-content:space-between;gap:2em;margin-bottom:1.6em}
${S} .psg-brand{display:flex;align-items:flex-start;gap:.6em}
${S} .psg-bwords{display:flex;flex-direction:column;align-items:stretch}
${S} .psg-bname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.4em;line-height:1;
  text-transform:uppercase;letter-spacing:.06em;color:var(--mb-text)}
${S} .psg-bname.mb-2nd{font-size:2em;letter-spacing:0;margin-top:.1em}
${S} .psg-stripe{display:block;height:.42em;margin-top:.5em;width:100%;min-width:10em;background:${STRIPE} 0 0/100% 100% no-repeat;
  border-radius:.1em}
${S} .psg-basil{width:4.6em;height:4.6em;flex-shrink:0;margin-top:-.8em;background:${BASIL} center/contain no-repeat;
  filter:drop-shadow(0 .3em .5em rgba(0,0,0,.4))}
${S} .psg-titles{text-align:end;padding-top:.5em}
${S} .psg-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.08em;color:var(--mb-primary)}
${S} .psg-title.mb-2nd{font-size:1.4em;letter-spacing:0}

${S} .psg-grid{flex:1;min-height:0;display:grid;gap:1.4em 2em;grid-template-columns:repeat(var(--cols,4),minmax(0,1fr));
  grid-template-rows:repeat(var(--rows,2),minmax(0,1fr));align-content:center}
${S} .psg-card{min-height:0;min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center;justify-content:center}
${S} .psg-phw{flex:1 1 0;min-height:0;width:100%;display:flex;align-items:center;justify-content:center}
${S} .psg-ph{position:relative;height:100%;max-height:17em;max-width:100%;aspect-ratio:1;border-radius:50%;
  background:var(--mb-surface);box-shadow:0 1.1em 2em rgba(0,0,0,.55)}
${S}.mb-portrait .psg-ph{max-height:15em}
${S} .psg-img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
${S} .psg-initial{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:4em;opacity:.3;
  font-family:var(--mb-font-heading)}
${S} .psg-badges{position:absolute;top:2%;inset-inline-end:0;display:flex;flex-direction:column;gap:.3em;align-items:flex-end}
${S} .psg-badge{background:var(--mb-accent);color:#fff;font-weight:800;font-size:.85em;padding:.25em .65em;border-radius:99em;
  white-space:nowrap;box-shadow:0 .3em .6em rgba(0,0,0,.35)}
${S} .psg-tag{position:absolute;top:4%;inset-inline-start:-4%;background:var(--mb-primary);color:var(--mb-bg);
  font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1em;text-transform:uppercase;letter-spacing:.06em;
  padding:.2em .7em;transform:rotate(-6deg);white-space:nowrap;box-shadow:0 .3em .6em rgba(0,0,0,.35)}
${S} .psg-names{margin-top:.75em;max-width:100%}
${S} .psg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;line-height:1.1;
  text-transform:uppercase;letter-spacing:.04em;color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .psg-name.mb-2nd{font-size:1.15em;letter-spacing:0;color:var(--mb-text);opacity:.9}
${S} .psg-desc{font-size:.8em;opacity:.65;margin-top:.15em;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .psg-prices{width:100%;max-width:12.5em;margin-top:.45em}
${S} .psg-row{display:flex;align-items:baseline;justify-content:space-between;gap:.8em;padding:.22em .1em;
  border-bottom:.09em solid color-mix(in srgb,var(--mb-text) 45%,transparent)}
${S} .psg-row:last-child{border-bottom:0}
${S} .psg-one{justify-content:center}
${S} .psg-lbl{font-weight:800;font-size:.85em;text-transform:uppercase;letter-spacing:.06em;opacity:.9;white-space:nowrap}
${S} .psg-amt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.3em;white-space:nowrap;letter-spacing:.03em}
${S} .psg-old{font-size:1em;opacity:.55;white-space:nowrap}

${S} .psg-panel{position:relative;width:30%;flex-shrink:0;background:var(--mb-accent);color:#fff;
  clip-path:polygon(24% 0,100% 0,100% 100%,0 100%,0 15%);
  background-image:linear-gradient(160deg,rgba(255,255,255,.08),transparent 40%,rgba(0,0,0,.18))}
${S}[dir=rtl] .psg-panel{clip-path:polygon(0 0,76% 0,100% 15%,100% 100%,0 100%)}
${S}.mb-portrait .psg-panel{width:auto;height:40em;clip-path:polygon(0 12%,100% 0,100% 100%,0 100%)}
${S} .psg-pin{position:absolute;inset:0;display:flex;flex-direction:column;padding:3.4em 3em 2.4em 4.4em}
${S}[dir=rtl] .psg-pin{padding:3.4em 4.4em 2.4em 3em}
${S}.mb-portrait .psg-pin{display:grid;grid-template-columns:1fr 22em;grid-template-rows:auto 1fr;column-gap:2.4em;padding:6.6em 3.2em 2.6em}
${S} .psg-phead{text-align:center;margin-bottom:1.2em}
${S}.mb-portrait .psg-phead{text-align:start;grid-column:1}
${S} .psg-ptitle{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:5.4em;line-height:1;
  text-transform:uppercase;letter-spacing:.04em}
${S} .psg-ptitle.mb-2nd{font-size:2.4em;letter-spacing:0}
${S} .psg-list{min-height:0}
${S}.mb-portrait .psg-list{grid-column:1}
${listCss(S, 'psgl', { name: '1.55em', price: '1.55em', desc: '.8em', leader: '.12em dotted rgba(255,255,255,.45)' })}
${S} .psgl-item{margin-bottom:1em}
${S} .psgl-name,${S} .psgl-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;
  letter-spacing:.04em;-webkit-line-clamp:1}
${S} .psgl-name2{font-size:1.15em}
${S} .psgl-desc{-webkit-line-clamp:1}
${S} .psgl-badge{background:var(--mb-primary);color:var(--mb-bg)}
${S} .psg-pphoto{flex:1 1 0;min-height:0;max-height:17em;margin-top:auto;padding-top:1.2em;display:flex;justify-content:center;align-items:flex-end}
${S}.mb-portrait .psg-pphoto{grid-column:2;grid-row:1/3;margin:0;padding:0;align-items:center}
${S} .psg-pimg{width:100%;max-width:24em;height:100%;max-height:14em;object-fit:cover;border-radius:1em;display:block;transform:rotate(-4deg);
  border:.35em solid rgba(255,255,255,.92);box-shadow:0 1.2em 2.2em rgba(0,0,0,.45)}
${S}.mb-portrait .psg-pphoto{max-height:none}
${S}.mb-portrait .psg-pimg{height:22em;max-height:none}
`,
};
